import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SearchGateway } from './search-gateway';
import { normalizePhone } from './phone-normalize';
import {
  CustomerCard,
  EntitySection,
  FacetBucket,
  UnifiedEntityResults,
  EntitySearchOptions,
} from './unified-search.model';

/**
 * Raw ES response shape from the CustomerSearch merged-doc index
 * (`POST /internalsearch/v1/search/customers`).
 */
interface CustomerSearchHit {
  _source: {
    customerId?: string;
    companyName?: string;
    name?: string;
    fullName?: string;
    brand?: string;
    status?: string;
    hasUserAccount?: boolean;
    hasPhoneService?: boolean;
    phoneNormalized?: string[];
    externID?: string;
    contract_street?: string;
    contract_zipCode?: string;
    contract_city?: string;
    entities?: LinkedEntity[];
  };
  inner_hits?: {
    /** Entities matched by the phone clause (default name = the nested path). */
    entities?: {
      hits: {
        hits: { _source: LinkedEntity }[];
      };
    };
    /** Entities matched by name/login/e-mail — named so it does not collide with the above. */
    identityMatch?: {
      hits: {
        hits: { _source: LinkedEntity }[];
      };
    };
  };
}

interface LinkedEntity {
  entityId?: string;
  type?: string;
  label?: string;
  status?: string;
  linkedUserAccountId?: string;
  login?: string;
  email?: string;
  ebueroEmails?: string[];
  phone?: string;
  phones?: string[];
  phoneNormalized?: string[];
}

interface AggBucket {
  key: string;
  doc_count: number;
}

interface CustomerSearchResponse {
  hits: {
    total: number | { value: number; relation: string };
    hits: CustomerSearchHit[];
  };
  aggregations?: Record<string, { buckets?: AggBucket[] }>;
}

function toBuckets(agg?: { buckets?: AggBucket[] }): FacetBucket[] {
  return (agg?.buckets ?? []).map((b) => ({ key: b.key, count: b.doc_count }));
}

function str(v: unknown): string {
  return v === null || v === undefined ? '' : String(v);
}

function buildAddress(src: CustomerSearchHit['_source']): string {
  const street = str(src.contract_street);
  const zip = str(src.contract_zipCode);
  const city = str(src.contract_city);
  const line2 = [zip, city].filter(Boolean).join(' ');
  return [street, line2].filter(Boolean).join(', ');
}

function toEntitySection(e: LinkedEntity): EntitySection {
  /* Surface the identifiers the backend matched on. Without them the card shows no reason why
   * this account was returned, and `childMatches` would filter it straight back out — the login
   * "torsten.lenz" is no substring of the label "Herr Torsten Lenz". */
  const identity = str(e.login) || str(e.email);
  return {
    kind: e.type === 'UA' ? 'useraccount' : 'phoneservice',
    entityId: str(e.entityId),
    label: str(e.label),
    status: str(e.status),
    ...(identity ? { sublabel: identity } : {}),
    ...(e.login ? { login: e.login } : {}),
    ...(e.email ? { email: e.email } : {}),
    ...(e.type === 'PS' && e.linkedUserAccountId
      ? { linkedUserAccountId: e.linkedUserAccountId }
      : {}),
  };
}

/**
 * Extract entities to display on the card. Both nested clauses report the entities they
 * matched via inner_hits — the phone clause under the default `entities` name, the
 * name/login/e-mail clause under `identityMatch` — so the card can show the user account
 * that actually answered the query instead of every account the customer owns. A customer
 * such as eGroup Holding has 80+ user accounts; listing all of them hides the one hit.
 *
 * Only a flat match (company or contract name, customer ID) leaves both absent; then there
 * is no "matching entity" to speak of and we fall back to `_source.entities`.
 *
 * When a matched entity is a PS, its linked UA is pulled in from `_source.entities` so the
 * card can pair them into complete member rows (UAs never appear in the phone inner_hits
 * because they carry no phone numbers).
 */
function resolveEntities(hit: CustomerSearchHit): LinkedEntity[] {
  const phoneMatches = hit.inner_hits?.entities?.hits?.hits ?? [];
  const identityMatches = hit.inner_hits?.identityMatch?.hits?.hits ?? [];
  const innerHits = [...phoneMatches, ...identityMatches];
  if (!innerHits.length) {
    return hit._source.entities ?? [];
  }

  const matched: LinkedEntity[] = [];
  const seen = new Set<string>();
  for (const { _source: entity } of innerHits) {
    const key = `${entity.type}:${entity.entityId}`;
    if (!seen.has(key)) {
      seen.add(key);
      matched.push(entity);
    }
  }

  const linkedUaIds = new Set(
    matched.filter((e) => e.type === 'PS' && e.linkedUserAccountId).map((e) => e.linkedUserAccountId!),
  );
  if (linkedUaIds.size > 0) {
    for (const e of hit._source.entities ?? []) {
      if (e.type === 'UA' && e.entityId && linkedUaIds.has(e.entityId) && !seen.has(`UA:${e.entityId}`)) {
        seen.add(`UA:${e.entityId}`);
        matched.push(e);
      }
    }
  }
  return matched;
}

function hitToCard(hit: CustomerSearchHit): CustomerCard | null {
  const src = hit._source;
  if (!src?.customerId) return null;
  const entities = resolveEntities(hit);
  return {
    customerId: src.customerId,
    name: str(src.companyName) || str(src.name) || str(src.fullName),
    contactName: str(src.fullName) || undefined,
    address: buildAddress(src),
    brand: str(src.brand),
    status: str(src.status),
    hasUserAccount: src.hasUserAccount ?? false,
    hasPhoneService: src.hasPhoneService ?? false,
    userAccounts: entities.filter((e) => e.type === 'UA').map(toEntitySection),
    phoneServices: entities.filter((e) => e.type === 'PS').map(toEntitySection),
  };
}

/** Heuristic: query is phone-like if >50% of non-whitespace chars are digits or phone separators. */
function isPhoneLike(query: string): boolean {
  const stripped = query.replace(/\s/g, '');
  if (stripped.length < 3) return false;
  const phoneChars = stripped.replace(/[^0-9+\-()/.]/g, '');
  return phoneChars.length / stripped.length > 0.5;
}

/** Max ES hits per query — same cap as MockSearchGateway. */
const MAX_HITS = 200;

const BRAND_FACET_SIZE = 50;
const STATUS_FACET_SIZE = 20;

/** Max matching user accounts / phone services reported per customer card. */
const MAX_MATCHED_ENTITIES = 10;

/**
 * Shortest normalized query that may carry the suffix wildcard. Below this the fragment is too
 * unselective to be a phone lookup — `*123` matched 177 nested entities on the test index — so it
 * would only pay the term-scan cost to return noise. The exact `term` clause still applies from
 * three digits up.
 */
const MIN_PHONE_SUFFIX_LENGTH = 6;

/**
 * Real search gateway querying the CustomerSearch merged-doc index.
 * Each ES hit is already a customer-centric document with embedded UA/PS
 * summaries — no client-side merge needed. Replaces MockSearchGateway
 * once the backend index is enriched (EB-4074).
 */
@Injectable()
export class RealSearchGateway extends SearchGateway {
  private readonly http = inject(HttpClient);

  search(query: string, options: EntitySearchOptions = {}): Observable<UnifiedEntityResults> {
    const escaped = query.replace(/[\\+\-|()~]/g, '\\$&');
    const phoneLike = isPhoneLike(query);

    const should: Record<string, unknown>[] = [
      {
        simple_query_string: {
          query: escaped,
          fields: ['customerId^5', 'companyName^3', 'name^3', 'fullName^2', 'externID', 'searchText'],
          lenient: true,
          default_operator: 'and',
        },
      },
      {
        simple_query_string: {
          query: escaped,
          fields: ['companyName.phonetic^2', 'name.phonetic^2', 'fullName.phonetic'],
          lenient: true,
          default_operator: 'and',
        },
      },
      /*
       * `entities` is mapped as `nested`, so these fields cannot be reached from a flat
       * simple_query_string — with `lenient: true` such a clause silently matches nothing
       * instead of erroring. Linked UA/PS names (e.g. a customer whose contract name differs
       * from the user account "Torsten Lenz (admin)") and user account logins are only
       * findable through an explicit nested wrapper.
       *
       * The nesting is also what keeps the results precise: every token has to match inside
       * one and the same entity, so a customer holding the unrelated logins "torsten.meyer"
       * and "anna.lenz" is not returned for "Torsten Lenz". For that reason the backend keeps
       * logins and e-mails out of the flat `searchText` field — dropping this clause would
       * silently disable username search altogether.
       *
       * `inner_hits` is named `identityMatch` so it does not collide with the phone clause
       * below, which uses the default name. It is what lets the card show the user account
       * that matched rather than all of the customer's accounts.
       */
      {
        nested: {
          path: 'entities',
          query: {
            simple_query_string: {
              query: escaped,
              fields: ['entities.label^2', 'entities.login^2', 'entities.email', 'entities.ebueroEmails'],
              lenient: true,
              default_operator: 'and',
            },
          },
          inner_hits: { name: 'identityMatch', _source: true, size: MAX_MATCHED_ENTITIES },
        },
      },
    ];

    if (phoneLike) {
      const normalized = normalizePhone(query);
      if (normalized.length >= 3) {
        const phoneShould: Record<string, unknown>[] = [
          { term: { 'entities.phoneNormalized': { value: normalized, boost: 10 } } },
        ];
        /*
         * The leading `*` cannot use the index — Elasticsearch walks the whole term dictionary of
         * `entities.phoneNormalized` per shard. It is kept deliberately, because it is what makes
         * two required cases work and the measured cost is small:
         *
         *  - Suffix lookup ("what customer owns a number ending 3388291?") is an explicit
         *    requirement of the EB-4074 spec (§ "Uses phoneNormalized field for phone suffix
         *    queries"); no exact `term` variant can answer it.
         *  - Nationally stored numbers. `PhoneNormalizer` only derives the leading-zero-stripped
         *    variant for numbers that arrive with a +49/0049 prefix, so a number indexed as
         *    "030…" keeps only its leading-zero form, while `normalizePhone()` here always strips
         *    the leading zero. Those entities are reachable through the wildcard alone —
         *    5,333 of 350,842 nested entities on tsrv (~1.5%).
         *
         * Measured on tsrv 2026-08-12 (143,948 customer docs / 350,842 nested entities /
         * ~227,856 distinct phone terms, 5 shards) via POST /internalsearch/v1/search/customers:
         * full gateway query with the wildcard `took` 11 ms cold and 5 ms warm; the isolated
         * nested clause `took` 16 ms cold vs 5 ms for the `term` alone. Re-measure if the index
         * grows by an order of magnitude, or move to a reversed phone field with a trailing
         * wildcard (backend change — internalsearch-api `CustomerSearch.LinkedEntity`).
         */
        if (normalized.length >= MIN_PHONE_SUFFIX_LENGTH) {
          phoneShould.push({
            wildcard: { 'entities.phoneNormalized': { value: `*${normalized}`, boost: 5 } },
          });
        }
        should.push({
          nested: {
            path: 'entities',
            query: { bool: { should: phoneShould } },
            inner_hits: { _source: true },
          },
        });
      }
    }

    const must: Record<string, unknown>[] = [];
    if (options.activeOnly) must.push({ term: { status: 'active' } });
    if (options.brands?.length) must.push({ terms: { brand: options.brands } });
    if (options.statuses?.length) must.push({ terms: { status: options.statuses } });

    if (options.kinds?.length) {
      const kindFilters: Record<string, unknown>[] = [];
      for (const kind of options.kinds) {
        if (kind === 'customer') {
          /* all docs are customers — no filter needed, but include for completeness */
        } else if (kind === 'useraccount') {
          kindFilters.push({ term: { hasUserAccount: true } });
        } else if (kind === 'phoneservice') {
          kindFilters.push({ term: { hasPhoneService: true } });
        }
      }
      if (kindFilters.length) {
        must.push({ bool: { should: kindFilters, minimum_should_match: 1 } });
      }
    }

    const boolQuery: Record<string, unknown> = {
      should,
      minimum_should_match: 1,
    };
    if (must.length) boolQuery['filter'] = must;

    const body: Record<string, unknown> = {
      query: { bool: boolQuery },
      aggregations: {
        brandAgg: { terms: { field: 'brand', size: BRAND_FACET_SIZE } },
        statusAgg: { terms: { field: 'status', size: STATUS_FACET_SIZE } },
      },
      size: MAX_HITS,
    };

    return this.http
      .post<CustomerSearchResponse>(`${environment.searchUrl}/search/customers`, body)
      .pipe(
        map((res) => {
          const total = typeof res.hits.total === 'number' ? res.hits.total : res.hits.total.value;
          const cards = res.hits.hits.map(hitToCard).filter((c): c is CustomerCard => c !== null);
          return {
            cards,
            total,
            facets: {
              brand: toBuckets(res.aggregations?.['brandAgg']),
              status: toBuckets(res.aggregations?.['statusAgg']),
              type: [] as FacetBucket[],
            },
          };
        }),
      );
  }
}
